import React, { useEffect, useCallback, useState } from "react";
import style from "../../assets/scss/sub/paymentHistory.module.scss";
import { useAppDispatch, useTimeConverter } from "../../hooks/hooks";
import { getOrdersAsync, getRefundOrderAsync } from "../../store/order/orderSlice";
import { useNavigate } from "react-router-dom";
import { PaymentInfoModal } from "../common/modal/PaymentInfoModal";
import { useUserAgent } from "../../hooks/hooks";
import {Trans, useTranslation} from "react-i18next";
import {useMyPageFunctions} from "./mypage/useMyPageFunctions";

export function PaymentHistory() {
  const { t } = useTranslation("payment");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { timeConverter } = useTimeConverter();
  const onClickVoucher = () => navigate(`/purchase-voucher`);
  const [orders, setOrders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const { isMobile } = useUserAgent();

  const { CommonPageField} = useMyPageFunctions();

  const getOrdersList = useCallback(
      async (page: number) => {
        try {
          const response: any = await dispatch(getOrdersAsync(page)).unwrap();
          const { content, totalPages, totalElements }: any = response || {};

          if (content) {
            setOrders(content);
            setTotalPages(totalPages > 0 ? totalPages : 1);
            setTotalOrders(totalElements);
          }
        } catch (error) {
          console.log(error);
        }
      },
      [dispatch]
  );

  const renderOrderStatus = (status: string, recallStatus: string) => {
    if (isMobile && status === "AWAITING_DEPOSIT") {
      return (
          <>
            입금
            <br />
            확인중
          </>
      );
    }

    // 이용권 회수에 대한 조건처리
    if (recallStatus === "RECALL") {
      return t('detail.row.recall');
    }else if (recallStatus === "PARTIALLY_RECALL") {
      if(status === "CANCELLED" ){
        return t('detail.row.refund');
      } else if (status === "PARTIALLY_CANCELLED") {
        return t('detail.row.partiallyCancel');
      } else {
        return t('detail.row.partiallyRecall');
      }
    }

    switch (status) {
      case "PAID":
        return t('detail.row.orderComplete');
      case "CANCELLED":
        return t('detail.row.refund');
      case "OUTSTANDING_PAYMENT":
        return t('detail.row.cancel');
      case "AWAITING_PAYMENT":
        return t('detail.row.awaitingPayment');
      case "AWAITING_DEPOSIT":
        return t('detail.row.checkVA');
      case "PARTIALLY_CANCELLED":
        return t('detail.row.partiallyCancel');
      default:
        return "-";
    }
  };

  const getRefundOrder = useCallback(
      async (orderIdx: number) => {
        try {
          const response: any = await dispatch(getRefundOrderAsync(orderIdx)).unwrap();

          if (response) {
            setSelectedOrder((prevOrder: any) => ({
              ...prevOrder,
              refundDetails: response,
              refundPrice: response[0].refundPrice,
              refundAt: timeConverter.convertToFormattedHHMMSS(
                  timeConverter.convertToLocalDate(response[0].createAt).split(" ")[0]
              ),
            }));
          }
        } catch (error) {
          console.log(error);
        }
      },
      [dispatch, timeConverter]
  );

  useEffect(() => {
    getOrdersList(currentPage);
  }, [getOrdersList, currentPage]);

  useEffect(() => {
    if (isModalOpen) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [isModalOpen]);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const handleOpenModal = (order: any) => {
    setSelectedOrder(order);
    if ((order.status === "CANCELLED") || (order.status === "PARTIALLY_CANCELLED")) getRefundOrder(order.idx);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  return (
      <div className={style.container}>
        <div className={style.containerContentBox}>
          <h2 className={style.containerTitle}>
            {t('title')}&nbsp;
            <span className={style.profileCount}>
            (<Trans t={t} i18nKey={"unit"} values={{orders : totalOrders}}/>)
          </span>
          </h2>
        </div>
        <div>
          <div className={style.labelContainer}>
            <div className={style.firstLabel}>{t('detail.column.orderedAt')}</div>
            <div className={style.secondLabel}>{t('detail.column.productName')}</div>
            <div className={style.thirdLabel}>{t('detail.column.quantity')}</div>
            <div className={style.fourthLabel}>{t('detail.column.orderStatus')}</div>
            <div className={style.fifthLabel}>{t('detail.column.orderDetail')}</div>
          </div>
          <div className={style.resultInfoContainer}>
            {orders.length > 0 ? (
                orders.map((order) => (
                    <div key={order.idx} className={style.resultInfoData}>
                      <div className={style.firstInfoData}>
                        {timeConverter.convertToFormattedDate(timeConverter.convertToLocalDate(order.createAt))}
                        <br /> {timeConverter.convertToLocalDate(order.createAt).split(" ")[1]}
                      </div>
                      <div className={style.secondInfoData}>{order.orderTitle}</div>
                      <div className={style.thirdInfoData}>{order.orderQuantity}</div>
                      <div
                          className={`${style.fourthInfoData} 
                  ${
                              order.status === "PAID"
                                ? style.paid
                                : order.status === "CANCELLED"
                                ? style.cancel
                                : order.status === "OUTSTANDING_PAYMENT"
                                ? style.outstanding
                                : order.status === "AWAITING_PAYMENT"
                                ? style.awaitingPayment
                                : order.status === "AWAITING_DEPOSIT"
                                ? style.awaitingDeposit
                                : order.status === "PARTIALLY_CANCELLED"
                                ? style.cancel
                                : order.recallStatus === "RECALL" // 회수에 대한 처리 
                                ? style.cancel
                                : order.recallStatus === "PARTIALLY_RECALL" // 뷰뷴 회수에 대한 처리 
                                ? style.cancel
                                : style.defaultStatus
                          }`}
                      >
                        {renderOrderStatus(order.status, order.recallStatus)}
                      </div>
                      <div className={style.fifthInfoData}>
                        <button className={style.guideButton} onClick={() => handleOpenModal(order)}>
                          {isMobile ? t('detail.btn.detailMo') : t('detail.btn.detail')}
                        </button>
                      </div>
                    </div>
                ))
            ) : (
                <div className={style.noPaymentInfo}>
                  {t('detail.row.emptyPayment1')}
                  <br />
                  {t('detail.row.emptyPayment2')}
                  <br />
                  <button className={style.buyVoucherButton} onClick={onClickVoucher}>
                    {t('detail.btn.purchaseVoucher')}
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                      <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.4986 14.2679L15.5 9.63393L11.4986 5L10.628 5.96365L13.2012 8.94359L5.74933 8.94359L5.74933 10.3198L13.2051 10.3198L10.628 13.3042L11.4986 14.2679Z"
                          fill="#583BE2"
                      />
                    </svg>
                  </button>
                </div>
            )}
          </div>

          <CommonPageField handlePageChange={handlePageChange} currentPage={currentPage} totalPages={totalPages} />
        </div>
        {isModalOpen && <PaymentInfoModal order={selectedOrder} onClose={handleCloseModal} />}
      </div>
  );
}
